// Derives the feedback heading from the spec's Rule -> UI Heading key. Most rows map by
// feedback_type. Two do not: profanity shares the prefilter type with rules that take a different
// heading, so it keys off rule_uid; and the three rules-based-1 labels are per-prompt content rules
// (~950 of them, with unnormalised names) that key off concept_uid instead.

import { isErrorFeedback, isFullOptimal, isTargetOptimal } from './optimalSignals'

import {
  BECAUSE,
  BUT,
  GEN_AI,
  GRAMMAR,
  ON_TASK,
  OPINION,
  PLAGIARISM,
  RULES_BASED_1,
  RULES_BASED_2,
  RULES_BASED_3,
  RUNON,
  SO,
  SPELLING,
} from '../../../constants/evidence'

const PREFILTER = 'prefilter'
// Stable: there are only four prefilter rules, and the others take Following Directions.
const PROFANITY_RULE_UID = 'fdee458a-f017-4f9a-a7d4-a72d1143abeb'
// Emitted by Evidence::Rule but absent from the frontend constants.
const GENERATIVE_LABEL = 'generativeLabel'
const LOW_CONFIDENCE = 'low-confidence'

export const FOLLOWING_DIRECTIONS = 'Following Directions'
export const AVOIDING_OPINION = 'Avoiding Opinion'
export const GRAMMAR_AND_SPELLING = 'Grammar and Spelling'
export const APPROPRIATE_LANGUAGE = 'Appropriate Language'
export const CLARITY_OF_IDEAS = 'Clarity of Ideas'
export const USING_BECAUSE = 'Using Because'
export const USING_SO = 'Using So'
export const REPETITION = 'Repetition'
// Not "That's a strong sentence!", which is the quail's line.
export const NICE_WORK = 'Nice work!'
export const ERROR_HEADER = 'Error'
export const DEFAULT_HEADER = 'Feedback'

// Scoped to rules-based-1: the repetition concept also appears under rules-based-2 and -3, which
// the spec heads differently.
const RULES_BASED_1_HEADING_BY_CONCEPT = {
  '6gQZPREURQQAaSzpIt_EEw': USING_BECAUSE, // Structure > Complex Sentences > Because
  'R3sBcYAvoXP2_oNVXiA98g': USING_SO, // Structure > Compound Sentences > So
  'TZ8N7NpExBJSapwhouAoQQ': USING_SO, // Structure > Complex Sentences > So That
  'N5VXCdTAs91gP46gATuvPQ': REPETITION, // Structure > Sentence Quality > Including Details From Prompt
}

const preciseEvidenceHeading = (prompt) => PRECISE_EVIDENCE_BY_CONJUNCTION[prompt?.conjunction] || DEFAULT_HEADER

const PRECISE_EVIDENCE_BY_CONJUNCTION = {
  [BECAUSE]: 'Using Precise Evidence to Demonstrate a Cause',
  [BUT]: 'Using Precise Evidence to Demonstrate a Contrast',
  [SO]: 'Using Precise Evidence to Demonstrate a Consequence',
}

const FOLLOWING_DIRECTIONS_TYPES = [ON_TASK, PLAGIARISM, PREFILTER]
// Both come out of the AI check alongside genAI and coach on the evidence, so they share its heading.
const PRECISE_EVIDENCE_TYPES = [GEN_AI, GENERATIVE_LABEL, LOW_CONFIDENCE, RULES_BASED_1, RULES_BASED_2]
const GRAMMAR_AND_SPELLING_TYPES = [GRAMMAR, SPELLING, RULES_BASED_3]

export const feedbackHeader = (response, prompt): string => {
  if (!response || !response.feedback_type) { return DEFAULT_HEADER }

  // Before isFullOptimal: error feedback carries optimal: true.
  if (isErrorFeedback(response)) { return ERROR_HEADER }
  if (isFullOptimal(response)) { return NICE_WORK }

  const { feedback_type, } = response

  if (response.rule_uid === PROFANITY_RULE_UID) { return APPROPRIATE_LANGUAGE }
  if (feedback_type === RUNON) { return CLARITY_OF_IDEAS }
  if (FOLLOWING_DIRECTIONS_TYPES.includes(feedback_type)) { return FOLLOWING_DIRECTIONS }
  if (feedback_type === OPINION) { return AVOIDING_OPINION }
  if (GRAMMAR_AND_SPELLING_TYPES.includes(feedback_type)) { return GRAMMAR_AND_SPELLING }
  if (feedback_type === RULES_BASED_1 && RULES_BASED_1_HEADING_BY_CONCEPT[response.concept_uid]) {
    return RULES_BASED_1_HEADING_BY_CONCEPT[response.concept_uid]
  }
  if (PRECISE_EVIDENCE_TYPES.includes(feedback_type)) { return preciseEvidenceHeading(prompt) }

  return DEFAULT_HEADER
}

// Max-attempts feedback replaces the prose, so it heads itself rather than taking the last rule's.
export const finalAttemptHeader = (response, prompt): string =>
  isTargetOptimal(response) ? GRAMMAR_AND_SPELLING : preciseEvidenceHeading(prompt)
